import styles from "./messenger-floating-window.module.css";

import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Tooltip } from "antd";

import CloseIcon from "@/icons/CloseIcon";
import MessengerFrame from "@/components/messenger/MessengerFrame";

const withOptionalTooltip = (enabled, tooltipProps, children) => {
  if (!enabled) {
    return children;
  }

  return <Tooltip {...tooltipProps}>{children}</Tooltip>;
};

const DEFAULT_MARGIN = 24;
const DEFAULT_WIDTH = 420;
const DEFAULT_HEIGHT = 720;
const NARROW_MAX_WIDTH = 560;

const getVisibleViewport = () => {
  const vv = window.visualViewport;
  const height = Math.round(vv?.height ?? window.innerHeight);
  const offsetTop = Math.round(vv?.offsetTop ?? 0);
  // Clamp so a flaky Firefox keyboard report cannot collapse the messenger to a sliver.
  const minHeight = isNarrowViewport() ? 180 : 280;
  return {
    height: Math.max(minHeight, height),
    offsetTop: Math.max(0, offsetTop),
  };
};

const getVisibleViewportHeight = () => getVisibleViewport().height;

const isNarrowViewport = () => window.innerWidth <= NARROW_MAX_WIDTH;

const getWindowSize = () => {
  const narrow = isNarrowViewport();
  const margin = narrow ? 16 : 32;
  const minHeight = narrow ? 280 : 520;
  const minWidth = narrow ? 280 : 340;
  const vh = getVisibleViewportHeight();

  return {
    width: Math.min(DEFAULT_WIDTH, Math.max(minWidth, window.innerWidth - margin)),
    height: Math.min(DEFAULT_HEIGHT, Math.max(minHeight, vh - margin)),
  };
};

const getDefaultPosition = () => ({
  x: Math.max(
    DEFAULT_MARGIN,
    window.innerWidth - getWindowSize().width - DEFAULT_MARGIN
  ),
  y: DEFAULT_MARGIN,
});

const clampPosition = ({ x, y }) => {
  const { width, height } = getWindowSize();
  const vh = getVisibleViewportHeight();

  return {
    x: Math.min(
      Math.max(DEFAULT_MARGIN, x),
      Math.max(DEFAULT_MARGIN, window.innerWidth - DEFAULT_MARGIN - width)
    ),
    y: Math.min(
      Math.max(DEFAULT_MARGIN, y),
      Math.max(DEFAULT_MARGIN, vh - DEFAULT_MARGIN - height)
    ),
  };
};

const COMPACT_MODE_STROKE = 2.2;
const COMPACT_MODE_ARROW_STROKE = COMPACT_MODE_STROKE * 0.85;

export const MobileModeIcon = ({ expanded = false, className }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={COMPACT_MODE_STROKE}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="4" y="4" width="16" height="16" rx="3" />
    <g strokeWidth={COMPACT_MODE_ARROW_STROKE}>
      {expanded ? (
        <>
          <path d="M10.2 10.2L7.2 7.2" />
          <path d="M7.2 9.8V7.2h2.6" />
          <path d="M13.8 13.8l3 3" />
          <path d="M16.8 14.2v2.6h-2.6" />
        </>
      ) : (
        <>
          <path d="M7 17L10.9 13.1" />
          <path d="M10.9 16.5v-3.4H7.5" />
          <path d="M17 7L13.1 10.9" />
          <path d="M13.1 7.5v3.4h3.4" />
        </>
      )}
    </g>
  </svg>
);

export const OpenMessengerTabIcon = ({ className }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={COMPACT_MODE_STROKE}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="4" y="4" width="16" height="16" rx="3" />
    <path d="M7 17L15.5 8" />
    <path d="M12.5 8h3v3" />
  </svg>
);

const MessengerFloatingWindow = ({
  open,
  mode = "floating",
  config,
  isMobile = false,
  onClose,
  onUnreadStateChange,
  onToggleMode,
  onOpenInNewTab,
  delegatedToTab = false,
  delegationChecked = true,
  handoffRequest = 0,
  onHandoffComplete,
  embedKey = 0,
  onReopenExternalTab,
  onContinueHere,
  takeoverPending = false,
  ownerToken = null,
}) => {
  const dragStartRef = useRef(null);
  const viewportRafRef = useRef(0);
  const [position, setPosition] = useState(() => getDefaultPosition());
  const [viewportHeight, setViewportHeight] = useState(() =>
    getVisibleViewportHeight()
  );
  const [viewportOffsetTop, setViewportOffsetTop] = useState(
    () => getVisibleViewport().offsetTop
  );

  const onMouseMove = useCallback((event) => {
    const dragStart = dragStartRef.current;
    if (!dragStart) {
      return;
    }

    event.preventDefault();
    setPosition(
      clampPosition({
        x: dragStart.windowX + event.clientX - dragStart.mouseX,
        y: dragStart.windowY + event.clientY - dragStart.mouseY,
      })
    );
  }, []);

  const onMouseUp = useCallback(() => {
    dragStartRef.current = null;
  }, []);

  useEffect(() => {
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [onMouseMove, onMouseUp]);

  useEffect(() => {
    const syncViewport = () => {
      if (viewportRafRef.current) {
        return;
      }
      viewportRafRef.current = window.requestAnimationFrame(() => {
        viewportRafRef.current = 0;
        const { height, offsetTop } = getVisibleViewport();
        setViewportHeight(height);
        setViewportOffsetTop(offsetTop);
        setPosition((current) => clampPosition(current));
      });
    };

    syncViewport();
    window.addEventListener("resize", syncViewport);
    window.visualViewport?.addEventListener("resize", syncViewport);
    window.visualViewport?.addEventListener("scroll", syncViewport);

    return () => {
      if (viewportRafRef.current) {
        window.cancelAnimationFrame(viewportRafRef.current);
        viewportRafRef.current = 0;
      }
      window.removeEventListener("resize", syncViewport);
      window.visualViewport?.removeEventListener("resize", syncViewport);
      window.visualViewport?.removeEventListener("scroll", syncViewport);
    };
  }, []);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const startDrag = useCallback(
    (event) => {
      if (event.button !== 0) {
        return;
      }

      dragStartRef.current = {
        mouseX: event.clientX,
        mouseY: event.clientY,
        windowX: position.x,
        windowY: position.y,
      };
    },
    [position.x, position.y]
  );

  const isFloating = mode === "floating";

  return (
    <>
      {open && !isFloating ? (
        <div
          className={styles.backdrop}
          aria-hidden="true"
          onClick={onClose}
        />
      ) : null}
    <div
      className={`${styles.window} ${isFloating ? styles.floating : styles.fixed}`}
      style={{
        display: open ? undefined : "none",
        "--messenger-vvh": `${viewportHeight}px`,
        ...(isFloating
          ? {
              left: position.x,
              top: position.y,
            }
          : {
              // Pin fixed messenger to the *visible* viewport (keyboard-aware).
              // Firefox Android often shrinks visualViewport.height while leaving
              // offsetTop > 0; without this the shell floats up and leaves a gap.
              top: viewportOffsetTop,
            }),
      }}
    >
      <div className={styles.header} onMouseDown={isFloating ? startDrag : undefined}>
        <div
          className={styles.leading}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <Button
            type="text"
            shape="circle"
            aria-label="Закрыть мессенджер"
            icon={<CloseIcon />}
            onClick={onClose}
          />
        </div>
        <div className={styles.title}>{config?.messenger_title || "Мессенджер"}</div>
        <div className={styles.actions} onMouseDown={(event) => event.stopPropagation()}>
          {withOptionalTooltip(
            !isMobile,
            {
              title: isFloating
                ? "Открыть в фиксированном режиме"
                : "Компактный режим",
            },
            <Button
              type="text"
              shape="circle"
              aria-label={
                isFloating
                  ? "Открыть мессенджер в фиксированном режиме"
                  : "Открыть мессенджер в компактном режиме"
              }
              icon={<MobileModeIcon expanded={isFloating} />}
              onClick={onToggleMode}
            />
          )}
          {withOptionalTooltip(
            !isMobile,
            {
              title: delegatedToTab
                ? "Мессенджер уже открыт в отдельной вкладке"
                : "Открыть мессенджер в отдельной вкладке",
            },
            <Button
              type="text"
              shape="circle"
              aria-label="Открыть мессенджер в отдельной вкладке"
              icon={<OpenMessengerTabIcon />}
              onClick={onOpenInNewTab}
              disabled={delegatedToTab}
            />
          )}
        </div>
      </div>
      <div className={styles.body}>
        <MessengerFrame
          key={embedKey}
          open={open}
          config={config}
          onUnreadStateChange={onUnreadStateChange}
          delegatedToTab={delegatedToTab}
          delegationChecked={delegationChecked}
          handoffRequest={handoffRequest}
          onHandoffComplete={onHandoffComplete}
          onReopenExternalTab={onReopenExternalTab}
          onContinueHere={onContinueHere}
          takeoverPending={takeoverPending}
          ownerToken={ownerToken}
        />
      </div>
    </div>
    </>
  );
};

export default MessengerFloatingWindow;
